import { ReactNode, useMemo } from 'react'
import { EllipsisIcon } from 'lucide-react'

import { TextTag, TextTagCategory } from '@campai/lib-common/utils/misc'

import { cn } from '@/components/new/lib/utils'
import { useIsMobile } from '@/components/new/hooks/use-mobile'
import { Button } from '@/components/new/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/new/ui/dropdown-menu'

import { RichTextEditorCopy } from './copy'
import { TextTagPicker } from './TextTagPicker'
import { getRichTextEditorActionDefinitions, RichTextEditorActionDefinition, RichTextEditorActionId } from './editor/actions'
import { RichTextEditorHandle, RichTextEditorMode } from './types'

type RichTextEditorToolbarProps = {
    api: RichTextEditorHandle
    allowTagInsert: boolean
    codeBlock: boolean
    compact: boolean
    imageUploadEnabled: boolean
    copy: RichTextEditorCopy
    headings: boolean
    hasTextSelection: boolean
    mode: RichTextEditorMode
    quote: boolean
    tags: Array<TextTag | TextTagCategory>
    taskList: boolean
    readOnly?: boolean
    renderToolbarEnd?: (api: RichTextEditorHandle) => ReactNode
    onRunAction: (actionId: RichTextEditorActionId) => void
}

type ToolbarActionGroup = {
    key: string
    actions: RichTextEditorActionDefinition[]
    disabled?: boolean
}

const ToolbarSection = ({
    actions,
    disabled,
    onRunAction,
}: {
    actions: RichTextEditorActionDefinition[]
    disabled?: boolean
    onRunAction: (actionId: RichTextEditorActionId) => void
}) => {
    if (actions.length === 0) {
        return null
    }

    return (
        <div
            data-slot="rich-text-editor-toolbar-group"
            className="bg-background border-border flex items-center rounded-lg border p-0.5 max-[768px]:contents"
        >
            {actions.map((action) => {
                const Icon = action.icon

                return (
                    <Button
                        key={action.id}
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="max-[768px]:h-11 max-[768px]:min-w-11 max-[768px]:flex-[1_0_calc(20%_-_0.25rem)]"
                        disabled={disabled}
                        aria-label={action.label}
                        title={action.label}
                        data-title-direction="bottom"
                        onClick={() => onRunAction(action.id)}
                    >
                        <Icon />
                    </Button>
                )
            })}
        </div>
    )
}

const CompactToolbarSection = ({
    groups,
    onRunAction,
}: {
    groups: ToolbarActionGroup[]
    onRunAction: (actionId: RichTextEditorActionId) => void
}) => {
    const visibleGroups = groups.filter((group) => group.actions.length > 0)

    if (visibleGroups.length === 0) {
        return null
    }

    return (
        <div
            data-slot="rich-text-editor-toolbar-group-compact"
            className="flex items-center gap-0.5 rounded-md p-0.5 max-[768px]:contents"
        >
            {visibleGroups.map((group, index) => (
                <div
                    key={group.key}
                    data-slot="rich-text-editor-toolbar-group-compact-section"
                    className={cn(
                        'flex items-center gap-0.5 max-[768px]:contents',
                        index > 0 && 'border-border/80 ml-1 border-l pl-1.5'
                    )}
                >
                    {group.actions.map((action) => {
                        const Icon = action.icon

                        return (
                            <Button
                                key={action.id}
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                className="max-[768px]:h-11 max-[768px]:min-w-11 max-[768px]:flex-[1_0_calc(20%_-_0.25rem)]"
                                disabled={group.disabled}
                                aria-label={action.label}
                                title={action.label}
                                data-title-direction="bottom"
                                onClick={() => onRunAction(action.id)}
                            >
                                <Icon />
                            </Button>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}

export const RichTextEditorToolbar = ({
    api,
    allowTagInsert,
    codeBlock,
    compact,
    imageUploadEnabled,
    copy,
    headings,
    hasTextSelection,
    mode,
    quote,
    tags,
    taskList,
    readOnly,
    renderToolbarEnd,
    onRunAction,
}: RichTextEditorToolbarProps) => {
    const isMobile = useIsMobile(768)
    const isRichMode = mode === 'rich' || mode === 'rich-inline'
    const actionDefinitions = useMemo(
        () =>
            isRichMode
                ? getRichTextEditorActionDefinitions({
                      codeBlock,
                      copy,
                      headings,
                      imageUploadEnabled,
                      mode,
                      quote,
                      taskList,
                  })
                : [],
        [codeBlock, copy, headings, imageUploadEnabled, isRichMode, mode, quote, taskList]
    )

    const actionGroups: ToolbarActionGroup[] = [
        {
            key: 'inline',
            actions: actionDefinitions.filter((action) => action.group === 'inline'),
            disabled: readOnly || !hasTextSelection,
        },
        {
            key: 'text',
            actions: actionDefinitions.filter((action) => action.group === 'text'),
            disabled: readOnly,
        },
        {
            key: 'lists',
            actions: actionDefinitions.filter((action) => action.group === 'lists'),
            disabled: readOnly,
        },
        {
            key: 'other',
            actions: actionDefinitions.filter((action) => action.group === 'other'),
            disabled: readOnly,
        },
    ]
    const hasLeftToolbar = actionGroups.some((group) => group.actions.length > 0)
    const hasRightToolbar = (allowTagInsert && tags.length > 0) || !!renderToolbarEnd
    const wrapPlaceholderInPanel = hasLeftToolbar || !!renderToolbarEnd
    const overflowActions =
        isMobile && actionDefinitions.length > 10
            ? actionGroups
                  .flatMap((group) => group.actions.map((action) => ({ ...action, disabled: group.disabled })))
                  .slice(9)
            : []
    const overflowActionIds = new Set(overflowActions.map((action) => action.id))
    const visibleActionGroups = actionGroups.map((group) => ({
        ...group,
        actions: group.actions.filter((action) => !overflowActionIds.has(action.id)),
    }))
    const overflowMenu = overflowActions.length > 0 ? (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size={compact ? 'icon-xs' : 'icon-sm'}
                    className="h-11 min-w-11 flex-[1_0_calc(20%_-_0.25rem)]"
                    disabled={readOnly}
                    aria-label={copy.toolbar.moreActions}
                    title={copy.toolbar.moreActions}
                    data-title-direction="bottom"
                >
                    <EllipsisIcon />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {overflowActions.map((action) => {
                    const Icon = action.icon

                    return (
                        <DropdownMenuItem
                            key={action.id}
                            className="min-h-11"
                            disabled={action.disabled}
                            onClick={() => onRunAction(action.id)}
                        >
                            <Icon />
                            {action.label}
                        </DropdownMenuItem>
                    )
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null

    if (!hasLeftToolbar && !hasRightToolbar) {
        return null
    }

    return (
        <div
            data-slot="rich-text-editor-toolbar"
            className={cn(
                'border-border bg-muted/30 flex items-start justify-between gap-2 border-b max-[768px]:flex-col',
                compact ? 'px-0 py-0' : 'px-2.5 py-2'
            )}
        >
            {compact ? (
                <div className="bg-muted/30 flex min-w-0 flex-1 flex-wrap items-center max-[768px]:w-full max-[768px]:gap-1">
                    <CompactToolbarSection groups={visibleActionGroups} onRunAction={onRunAction} />
                    {overflowMenu}
                </div>
            ) : (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 max-[768px]:w-full">
                    {visibleActionGroups.map((group) => (
                        <ToolbarSection
                            key={group.key}
                            actions={group.actions}
                            disabled={group.disabled}
                            onRunAction={onRunAction}
                        />
                    ))}
                    {overflowMenu}
                </div>
            )}
            <div className="flex shrink-0 items-center gap-1.5 max-[768px]:w-full max-[768px]:flex-wrap max-[768px]:empty:hidden">
                {renderToolbarEnd ? <div className="flex items-center gap-2">{renderToolbarEnd(api)}</div> : null}
                {allowTagInsert && tags.length > 0 ? (
                    <div
                        className={cn(
                            'max-[768px]:w-full',
                            wrapPlaceholderInPanel && 'bg-background border-border rounded-lg border p-0.5'
                        )}
                    >
                        <TextTagPicker
                            disabled={readOnly}
                            copy={copy}
                            tags={tags}
                            onSelect={api.insertTag}
                            panelStyle={wrapPlaceholderInPanel}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    )
}
